/* eslint-disable no-unused-vars */
/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Box } from '@chakra-ui/react';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useParams } from 'react-router-dom';
import _ from 'lodash';
import { useFormValidationToast } from '../../../services/actionToast';
import { InputForm } from '../../inputs/forms/InputForm';
import { useFetch } from '../../../hooks/useFetch';
import { SwitchInputForm } from '../../inputs/forms/SwitchInputForm';
import { FormActions, FormRow, FormSection, FormShell } from '../../ui';
import { ImageInputForm } from '../../inputs/forms/ImageInputForm';
import { ColorInputForm } from '../../inputs/forms/ColorInputForm';
import { NumberInputForm } from '../../inputs/forms/NumberInputForm';

interface ContentCategoryFormProps {
  defaultValues?: any;
}

interface FormInputsProps {
  name: string;
  value: string;
  placeholder?: string;
  label: string;
  yup: any;
}

export default function ContentCategoryForm({ defaultValues = {} }: ContentCategoryFormProps) {
  const validationToast = useFormValidationToast();
  const baseUrl = '/v1/admin/categories';
  const { requestApi } = useFetch(baseUrl);
  const [images, setImages] = useState<any>('');
  const [uploading, setUploading] = useState<any>(false);
  const { itemId } = useParams();
  const navigate = useNavigate();
  const yupschema: any = {};
  const defaultValuesSchema: any = {};

  const formInputs: FormInputsProps[] = [
    {
      name: 'name',
      value: defaultValues.name ?? '',
      placeholder: 'Nome da categoria',
      label: 'Nome',
      yup: Yup.string().required('Este campo é obrigatório!'),
    },
    {
      name: 'hex_color',
      value: defaultValues.hex_color ?? '#000000',
      placeholder: 'Código da cor',
      label: 'Cor',
      yup: Yup.string().required('Este campo é obrigatório!'),
    },
    {
      name: 'image',
      value: defaultValues?.image?.original ?? null,
      placeholder: '',
      label: 'Imagem',
      yup: Yup.mixed().nullable(),
    },
    {
      name: 'status',
      value: defaultValues.status ?? true,
      label: 'Status',
      yup: Yup.boolean(),
    },
    {
      name: 'position',
      value: defaultValues.position ?? 0,
      placeholder: '0',
      label: 'Ordem (menor aparece primeiro)',
      yup: Yup.number()
        .typeError('Informe um número inteiro')
        .integer('Informe um número inteiro')
        .min(0, 'Ordem deve ser >= 0')
        .required('Este campo é obrigatório!'),
    },
  ];

  formInputs.forEach((object) => {
    yupschema[object.name] = object.yup;
    defaultValuesSchema[object.name] = object.value;
  });
  const schema = Yup.object().shape(yupschema);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValuesSchema,
  });

  const name = formInputs.find((field) => field.name === 'name');
  const color = formInputs.find((field) => field.name === 'hex_color');
  const image = formInputs.find((field) => field.name === 'image');
  const status = formInputs.find((field) => field.name === 'status');
  const position = formInputs.find((field) => field.name === 'position');

  const onSubmit = async (values: any) => {
    const params = {
      ...values,
      image: _,
    };
    const { status: resStatus, data } = await requestApi(
      defaultValues.name ? 'put' : 'post',
      params,
      defaultValues.name ? `${baseUrl}/${itemId}` : null
    );
    if ((resStatus === 200 || resStatus === 201) && images) {
      setUploading(true);
      const apiUrlId = itemId ?? data?.data?.id;
      await requestApi('put', { file: images.toString() }, `${baseUrl}/${apiUrlId}/images`);
      setUploading(false);
    }
    if (resStatus === 200 || resStatus === 201) navigate('/contents/categories');
  };

  useEffect(() => {
    if (defaultValues.name) {
      reset(defaultValuesSchema);
    }
  }, [defaultValues]);

  const isEdit = Boolean(defaultValues.name);

  return (
    <FormShell
      title={isEdit ? 'Editar Categoria' : 'Criar Categoria'}
      backTo="/contents/categories"
      backLabel="Categorias"
    >
      <Box
        as="form"
        noValidate
        onSubmit={handleSubmit(onSubmit, validationToast)}
        display="flex"
        flexDirection="column"
        gap="16px"
      >
        <FormSection title="Informações Básicas">
          <FormRow>
            <InputForm
              name={name?.name}
              label={name?.label}
              placeholder={name?.placeholder}
              isRequired
              register={register}
              errors={errors}
            />
            <ColorInputForm
              name={color?.name}
              label={color?.label}
              placeholder={color?.placeholder}
              isRequired
              register={register}
              errors={errors}
            />
            <NumberInputForm
              name={position?.name}
              label={position?.label}
              placeholder={position?.placeholder}
              isRequired
              control={control}
              errors={errors}
              min={0}
            />
            <SwitchInputForm
              name={status?.name}
              label={status?.label}
              errors={errors}
              control={control}
            />
          </FormRow>

          <Box>
            <ImageInputForm
              compact={false}
              defaultVal={defaultValues?.image}
              setImages={setImages}
              setIsUpping={setUploading}
              name={image?.name}
              label={image?.label}
              register={register}
              watch={watch}
              errors={errors}
            />
          </Box>
        </FormSection>

        <FormActions cancelTo="/contents/categories" isLoading={isSubmitting || uploading} />
      </Box>
    </FormShell>
  );
}
